import { z } from "zod/v4"
import { CreatedAt, UpdatedAt } from "./system"
import { PersonalNotificationIcon } from "./personal-notification-icon"
import { DocumentSnapshotLike } from "../firestore"
import { BulkDeliveryMasterId, BulkDeliveryTargetChunkId, ItemIdSchema, UserIdSchema } from "../id"
import { Scope } from "./coin"
import { assertNever } from "../lib/assert-never"

export enum BulkDeliveryMasterKind {
  Notification = "notification",
  Compensation = "compensation",
  Confiscation = "confiscation",
  Push = "push"
}

export function getBulkDeliveryMasterKindLabel(kind: BulkDeliveryMasterKind): string {
  switch (kind) {
    case BulkDeliveryMasterKind.Notification:
      return "あなた宛のお知らせ"
    case BulkDeliveryMasterKind.Compensation:
      return "補填"
    case BulkDeliveryMasterKind.Confiscation:
      return "コイン回収"
    case BulkDeliveryMasterKind.Push:
      return "Push配信"
    // no default
  }
}

export const BulkDeliveryTargetSetting = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("all_users"),
    with_new_user: z.object({
      until: z.number().describe("いつまでの新規ユーザーを対象とするか（UNIX Timestamp mills）"),
    }).optional().describe("設定された場合、welcome-delivery-masterを自動作成"),
  }),
  z.object({
    kind: z.literal("specified_users"),
    user_ids: z.array(z.string().min(1)),
  }),
])

export type BulkDeliveryTargetSetting = z.infer<typeof BulkDeliveryTargetSetting>

const BulkDeliveryMasterCommon = z.object({
  execution_start_at: z.number().describe("実行開始時間 (UNIX Timestamp)"),
  target_setting: BulkDeliveryTargetSetting.describe("対象設定"),
  estimated_total_user_count: z.number().nullable().describe("推定対象ユーザー総数（all_users時はprepareが完了するまでnull、指定ユーザー時はチャンクのuser_count総和）"),
})

const BulkDeliveryDeletionSupportBase = z.object({
  deletion_start_at: z.number().nullable().describe("削除開始時間 (UNIX Timestamp、nullの場合は削除しない)"),
})

export const PersonalNotificationContent = z.object({
  title: z.string().describe("タイトル"),
  icon: PersonalNotificationIcon.describe("アイコン"),
  content: z.any().describe("QuillDelta形式のリッチテキスト"),
})

export type PersonalNotificationContent = z.infer<typeof PersonalNotificationContent>

export const NotificationDeliveryMasterMeta = BulkDeliveryMasterCommon.extend(BulkDeliveryDeletionSupportBase.shape).extend({
  kind: z.literal(BulkDeliveryMasterKind.Notification),
  payload: z.object({
    notification: PersonalNotificationContent,
  }),
})

export type NotificationDeliveryMasterMeta = z.infer<typeof NotificationDeliveryMasterMeta>

const CompensationDeliveryContentCoin = z.object({
  kind: z.literal("coin"),
  coin: z.object({
    scope: z.nativeEnum(Scope).describe("コインスコープ"),
    amount: z.number().describe("金額"),
    expiry: z.number().nullable().optional().describe("コインの有効期限（UNIXタイムスタンプミリ秒、null可）"),
  }),
})

const CompensationDeliveryContentItem = z.object({
  kind: z.literal("item"),
  item: z.object({
    item_ids: z.array(ItemIdSchema).min(1).describe("配布するアイテムIDのリスト"),
  }),
})

const CompensationDeliveryContent = z.discriminatedUnion("kind", [
  CompensationDeliveryContentCoin,
  CompensationDeliveryContentItem,
])

export const LocalizedLabels = z.object({
  name: z.record(z.string(), z.string()).optional().describe("多言語対応の補填名 (例: { ja: '補填名' })"),
}).describe("多言語対応のラベル")

export type LocalizedLabels = z.infer<typeof LocalizedLabels>

export const CompensationDeliveryMasterMeta = BulkDeliveryMasterCommon.extend({
  name: z.string().min(1).max(20).describe("補填名"),
  labels: LocalizedLabels.optional(),
  kind: z.literal(BulkDeliveryMasterKind.Compensation),
  payload: z.object({
    receive_method: z.enum(["button_required", "auto_grant"]).describe("受け取り方法"),
    expire_at: z.number().optional().describe("受け取り期限（button_requiredの場合のみ）"),
    notification: PersonalNotificationContent.optional().describe("お知らせ（省略時はサイレント補填）"),
    delivery_content: CompensationDeliveryContent,
  }),
})

export type CompensationDeliveryMasterMeta = z.infer<typeof CompensationDeliveryMasterMeta>

export const ConfiscationDeliveryMasterMeta = BulkDeliveryMasterCommon.extend({
  name: z.string().describe("回収名"),
  kind: z.literal(BulkDeliveryMasterKind.Confiscation),
  payload: z.object({
    notification: PersonalNotificationContent,
    delivery_content: z.object({
      coin: z.object({
        scope: z.enum(Scope).describe("コインスコープ"),
        amount: z.number().describe("金額"),
      }),
    }),
  }),
})

export type ConfiscationDeliveryMasterMeta = z.infer<typeof ConfiscationDeliveryMasterMeta>

export enum PushActionScreenType {
  SystemNotification = "system_notification",
  PersonalNotification = "personal_notification",
  PersonalNotificationPresent = "personal_notification_present",
  Look = "look",
  Shop = "shop",
}

export function convertPushActionScreenTypeToDisplayString(screenType: PushActionScreenType): string {
  switch (screenType) {
    case PushActionScreenType.SystemNotification:
      return "運営からのお知らせTOP"
    case PushActionScreenType.PersonalNotification:
      return "あなた宛のお知らせTOP"
    case PushActionScreenType.PersonalNotificationPresent:
      return "あなた宛のお知らせ プレゼントタブ"
    case PushActionScreenType.Look:
      return "アカウント画面 ホロスーツ"
    case PushActionScreenType.Shop:
      return "SHOP TOP"
    default:
      return assertNever(screenType)
  }
}

export const PushDeliveryAction = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("url").describe("URL遷移"),
    value: z.string().url().describe("遷移先URL"),
  }),
  z.object({
    kind: z.literal("screen").describe("画面遷移"),
    screen: z.enum(PushActionScreenType).describe("遷移先画面"),
  }),
])

export type PushDeliveryAction = z.infer<typeof PushDeliveryAction>

export const PushDeliveryPayload = z.object({
  title: z.string().min(1).describe("通知タイトル（推奨20文字）"),
  body: z.string().min(1).describe("通知本文（推奨100文字）"),
  managed_image_id: z.string().nullable().describe("ManagedImageId"),
  action: PushDeliveryAction.nullable().describe("タップ時のアクション（nullの場合は遷移なし）"),
})

export type PushDeliveryPayload = z.infer<typeof PushDeliveryPayload>

export const PushDeliveryMasterMeta = BulkDeliveryMasterCommon.extend({
  kind: z.literal(BulkDeliveryMasterKind.Push),
  payload: PushDeliveryPayload,
})

export type PushDeliveryMasterMeta = z.infer<typeof PushDeliveryMasterMeta>

export const BulkDeliveryMasterDocumentMeta = z.discriminatedUnion("kind", [
  NotificationDeliveryMasterMeta,
  CompensationDeliveryMasterMeta,
  ConfiscationDeliveryMasterMeta,
  PushDeliveryMasterMeta,
])

export type BulkDeliveryMasterDocumentMeta = z.infer<typeof BulkDeliveryMasterDocumentMeta>

export enum BulkDeliveryMasterStatus {
  Draft = "draft",
  Scheduled = "scheduled",
  Preparing = "preparing",
  Delivering = "delivering",
  DeliveryCompleted = "delivery_completed",
  Deleting = "deleting",
  Deleted = "deleted"
}


export const BulkDeliveryMasterDocumentBody = BulkDeliveryMasterDocumentMeta.and(z.object({
  status: z.enum(BulkDeliveryMasterStatus).describe("ステータス"),
  reference_managed_image_ids: z.array(z.string()).describe("使用している画像のIDリスト（自動計算）"),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
}))

export type BulkDeliveryMasterDocumentBody = z.infer<typeof BulkDeliveryMasterDocumentBody>

export type BulkDeliveryMaster<K extends BulkDeliveryMasterKind = BulkDeliveryMasterKind> = BulkDeliveryMasterDocumentBody & {
  id: BulkDeliveryMasterId
  kind: K
}

export class BulkDeliveryMasterDocuments {

  /** PUSH通知画像の CDN 変換パラメータ: 512px, q-15, f-png */
  static readonly PUSH_IMAGE_TRANSFORMATION = "w-512,q-15,f-png"

  static toDocumentData(master: BulkDeliveryMaster): object {
    return {
      ...master,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): BulkDeliveryMaster | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    const baseFields = {
      id: BulkDeliveryMasterId(doc.id),
      kind: data["kind"],
      execution_start_at: data["execution_start_at"],
      target_setting: data["target_setting"],
      estimated_total_user_count: data["estimated_total_user_count"] ?? null,
      status: data["status"],
      reference_managed_image_ids: data["reference_managed_image_ids"] || [],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }

    // kind別の特有フィールドを追加
    if (data["kind"] === BulkDeliveryMasterKind.Notification) {
      return {
        ...baseFields,
        kind: BulkDeliveryMasterKind.Notification,
        payload: data["payload"],
        deletion_start_at: data["deletion_start_at"],
      } satisfies BulkDeliveryMaster<BulkDeliveryMasterKind.Notification>
    } else if (data["kind"] === BulkDeliveryMasterKind.Compensation) {
      const payload = data["payload"]

      // 旧形式（item.item_id）から新形式（item.item_ids）への変換（後方互換性）
      if (payload?.delivery_content?.kind === "item" && payload.delivery_content.item) {
        const item = payload.delivery_content.item
        if ("item_id" in item && !("item_ids" in item)) {
          // 旧形式を検出: item_idを配列に変換
          payload.delivery_content.item = {
            item_ids: [item.item_id]
          }
        }
      }

      return {
        ...baseFields,
        kind: BulkDeliveryMasterKind.Compensation,
        name: data["name"],
        labels: data["labels"],
        payload,
      } satisfies BulkDeliveryMaster<BulkDeliveryMasterKind.Compensation>
    } else if (data["kind"] === BulkDeliveryMasterKind.Confiscation) {
      return {
        ...baseFields,
        kind: BulkDeliveryMasterKind.Confiscation,
        name: data["name"],
        payload: data["payload"],
      } satisfies BulkDeliveryMaster<BulkDeliveryMasterKind.Confiscation>
    } else if (data["kind"] === BulkDeliveryMasterKind.Push) {
      return {
        ...baseFields,
        kind: BulkDeliveryMasterKind.Push,
        payload: data["payload"],
      } satisfies BulkDeliveryMaster<BulkDeliveryMasterKind.Push>
    }

    return null
  }
}

export const ChunkProcessResult = z.object({
  successful_count: z.number().describe("成功した件数"),
  failed_count: z.number().describe("失敗した件数"),
  skipped_count: z.number().describe("スキップした件数"),
  failed_user_ids: z.array(z.string()).describe("失敗したユーザーIDリスト"),
  skipped_user_ids: z.array(z.string()).describe("スキップしたユーザーIDリスト（存在しないユーザー等）"),
  completed_at: z.number().describe("完了時刻 (UNIX Timestamp)"),
})

export type ChunkProcessResult = z.infer<typeof ChunkProcessResult>

export const BulkDeliveryTargetChunkMeta = z.object({
  user_ids: z.array(z.string()).describe("対象ユーザーIDリスト（1000件程度ずつのチャンク）"),
  user_count: z.number().describe("ユーザー数"),
  bulk_delivery_master_id: z.string().transform(BulkDeliveryMasterId).describe("非正規化：親のマスターID"),
  bulk_delivery_master_kind: z.enum(BulkDeliveryMasterKind).describe("非正規化：親のマスター種別"),
})

export type BulkDeliveryTargetChunkMeta = z.infer<typeof BulkDeliveryTargetChunkMeta>

export enum BulkDeliveryTargetChunkStatus {
  Pending = "pending",
  Delivering = "delivering",
  DeliveryCompleted = "delivery_completed",
  Deleting = "deleting",
  Deleted = "deleted"
}


export const BulkDeliveryTargetChunkDocumentBody = BulkDeliveryTargetChunkMeta.extend({
  status: z.enum(BulkDeliveryTargetChunkStatus).describe("処理ステータス"),
  visible_start_at: z.number().describe("いつ以降処理可能か（この時間より前は取得されない）"),
  try_count: z.number().describe("処理試行回数"),
  delivery_result: ChunkProcessResult.optional().describe("配信結果"),
  deletion_result: ChunkProcessResult.optional().describe("削除結果"),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type BulkDeliveryTargetChunkDocumentBody = z.infer<typeof BulkDeliveryTargetChunkDocumentBody>

export type BulkDeliveryTargetChunk = BulkDeliveryTargetChunkDocumentBody & {
  id: BulkDeliveryTargetChunkId
}

export class BulkDeliveryTargetChunkDocuments {
  static toDocumentData(chunk: BulkDeliveryTargetChunk): object {
    return {
      ...chunk,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): BulkDeliveryTargetChunk | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: BulkDeliveryTargetChunkId(doc.id),
      user_ids: data["user_ids"],
      user_count: data["user_count"],
      bulk_delivery_master_id: BulkDeliveryMasterId(data["bulk_delivery_master_id"]),
      bulk_delivery_master_kind: data["bulk_delivery_master_kind"],
      status: data["status"],
      visible_start_at: data["visible_start_at"] ?? 0,
      try_count: data["try_count"] ?? 0,
      delivery_result: data["delivery_result"],
      deletion_result: data["deletion_result"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

// API リクエスト用（user_idsを含む）
export const BulkDeliveryTargetSettingAllUsersRequest = z.object({
  kind: z.literal("all_users").describe("対象設定"),
  withNewUser: z.object({
    until: z.number().describe("いつまでの新規ユーザーを対象とするか（UNIX Timestamp mills）"),
  }).optional().describe("設定された場合、welcome-delivery-masterを自動作成"),
})

export const BulkDeliveryTargetSettingSpecifiedUsersRequest = z.object({
  kind: z.literal("specified_users").describe("対象設定"),
  userIds: z.array(UserIdSchema).describe("対象ユーザーIDリスト"),
})

export const BulkDeliveryTargetSettingRequest = z.discriminatedUnion("kind", [
  BulkDeliveryTargetSettingAllUsersRequest,
  BulkDeliveryTargetSettingSpecifiedUsersRequest,
])

export type BulkDeliveryTargetSettingRequest = z.infer<typeof BulkDeliveryTargetSettingRequest>

export const CreateNotificationDeliveryMasterRequest = z.object({
  kind: z.literal(BulkDeliveryMasterKind.Notification),
  executionStartAt: z.number().describe("実行開始時間 (UNIX Timestamp)"),
  deletionStartAt: z.number().nullable().describe("削除開始時間 (UNIX Timestamp、nullの場合は削除しない)"),
  targetSetting: BulkDeliveryTargetSettingRequest,
  payload: z.object({
    notification: z.object({
      title: z.string().describe("タイトル"),
      icon: PersonalNotificationIcon.describe("アイコン"),
      content: z.any().describe("QuillDelta形式のリッチテキスト"),
    }),
  }),
})

export type CreateNotificationDeliveryMasterRequest = z.infer<typeof CreateNotificationDeliveryMasterRequest>

const CompensationDeliveryContentCoinRequest = z.object({
  kind: z.literal("coin"),
  coin: z.object({
    scope: z.enum(Scope).describe("コインスコープ"),
    amount: z.int().min(1).max(99_999).describe("金額"),
    expiry: z.number().nullable().optional().describe("コインの有効期限（UNIXタイムスタンプミリ秒、null可）"),
  }),
})

const CompensationDeliveryContentItemRequest = z.object({
  kind: z.literal("item"),
  item: z.object({
    itemIds: z.array(ItemIdSchema).min(1).describe("配布するアイテムIDのリスト"),
  }),
})

const CompensationDeliveryContentRequest = z.discriminatedUnion("kind", [
  CompensationDeliveryContentCoinRequest,
  CompensationDeliveryContentItemRequest,
])

export const CreateCompensationDeliveryMasterRequest = z.object({
  kind: z.literal(BulkDeliveryMasterKind.Compensation),
  name: z.string().min(1).max(20).describe("補填名"),
  labels: LocalizedLabels.optional(),
  executionStartAt: z.number().describe("実行開始時間 (UNIX Timestamp)"),
  targetSetting: BulkDeliveryTargetSettingRequest,
  payload: z.object({
    receiveMethod: z.enum(["button_required", "auto_grant"]).describe("受け取り方法"),
    expireAt: z.number().optional().describe("受け取り期限（button_requiredの場合のみ）"),
    notification: z.object({
      title: z.string().describe("お知らせタイトル"),
      icon: PersonalNotificationIcon.describe("アイコン"),
      content: z.any().describe("QuillDelta形式のリッチテキスト"),
    }).optional().describe("お知らせ（省略時はサイレント補填）"),
    deliveryContent: CompensationDeliveryContentRequest,
  }),
})

export type CreateCompensationDeliveryMasterRequest = z.infer<typeof CreateCompensationDeliveryMasterRequest>

export const CreateConfiscationDeliveryMasterRequest = z.object({
  kind: z.literal(BulkDeliveryMasterKind.Confiscation),
  name: z.string().min(1).max(20).describe("回収名"),
  executionStartAt: z.number().describe("実行開始時間 (UNIX Timestamp)"),
  targetSetting: BulkDeliveryTargetSettingRequest,
  payload: z.object({
    notification: z.object({
      title: z.string().describe("お知らせタイトル"),
      icon: PersonalNotificationIcon.describe("アイコン"),
      content: z.any().describe("QuillDelta形式のリッチテキスト"),
    }),
    deliveryContent: z.object({
      coin: z.object({
        scope: z.enum(Scope).describe("コインスコープ"),
        amount: z.number().int().min(1).max(99999).describe("回収する金額（正の値）"),
      }),
    }),
  }),
})

export type CreateConfiscationDeliveryMasterRequest = z.infer<typeof CreateConfiscationDeliveryMasterRequest>

export const PushDeliveryActionRequest = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("url"),
    value: z.string().url(),
  }),
  z.object({
    kind: z.literal("screen"),
    screen: z.enum(PushActionScreenType),
  }),
])

export type PushDeliveryActionRequest = z.infer<typeof PushDeliveryActionRequest>

export const CreatePushDeliveryMasterRequest = z.object({
  kind: z.literal(BulkDeliveryMasterKind.Push),
  executionStartAt: z.number().describe("配信開始時間 (UNIX Timestamp)"),
  targetSetting: BulkDeliveryTargetSettingRequest,
  payload: z.object({
    title: z.string().min(1),
    body: z.string().min(1),
    managedImageId: z.string().nullable().describe("ManagedImageId"),
    action: PushDeliveryActionRequest.nullable(),
  }),
})

export type CreatePushDeliveryMasterRequest = z.infer<typeof CreatePushDeliveryMasterRequest>

export const CreateBulkDeliveryMasterRequest = z.discriminatedUnion("kind", [
  CreateNotificationDeliveryMasterRequest,
  CreateCompensationDeliveryMasterRequest,
  CreateConfiscationDeliveryMasterRequest,
  CreatePushDeliveryMasterRequest,
])

export type CreateBulkDeliveryMasterRequest = z.infer<typeof CreateBulkDeliveryMasterRequest>

export const UpdateBulkDeliveryMasterStatusRequest = z.object({
  status: z.union([
    z.literal(BulkDeliveryMasterStatus.Draft),
    z.literal(BulkDeliveryMasterStatus.Scheduled)
  ]).describe("変更するステータス"),
})

export type UpdateBulkDeliveryMasterStatusRequest = z.infer<typeof UpdateBulkDeliveryMasterStatusRequest>

export const UpdateNotificationDeliveryMasterContentRequest = z.object({
  payload: z.object({
    notification: z.object({
      title: z.string().describe("通知タイトル"),
      icon: PersonalNotificationIcon.describe("通知アイコン"),
      content: z.any().describe("QuillDelta形式のリッチテキスト"),
    }),
  }),
  executionStartAt: z.number().describe("配信開始日時 (UNIX Timestamp mills)"),
  deletionStartAt: z.number().nullable().describe("削除開始日時 (UNIX Timestamp mills、nullの場合は削除しない)"),
})

export type UpdateNotificationDeliveryMasterContentRequest = z.infer<typeof UpdateNotificationDeliveryMasterContentRequest>

export const UpdateBulkDeliveryMasterDeletionRequest = z.object({
  deletionStartAt: z.number().nullable().describe("削除開始日時 (UNIX Timestamp mills、nullの場合は削除しない)"),
})

export type UpdateBulkDeliveryMasterDeletionRequest = z.infer<typeof UpdateBulkDeliveryMasterDeletionRequest>

export const UpdateCompensationDeliveryMasterContentRequest = z.object({
  name: z.string().min(1).max(20).describe("補填名"),
  labels: LocalizedLabels.optional(),
  executionStartAt: z.number().describe("配信開始日時 (UNIX Timestamp mills)"),
  payload: z.object({
    receiveMethod: z.enum(["button_required", "auto_grant"]).describe("受け取り方法"),
    expireAt: z.number().optional().describe("受け取り期限（button_requiredの場合のみ）"),
    notification: z.object({
      title: z.string().describe("お知らせタイトル"),
      icon: PersonalNotificationIcon.describe("アイコン"),
      content: z.any().describe("QuillDelta形式のリッチテキスト"),
    }).optional().describe("お知らせ（省略時はサイレント補填）"),
    deliveryContent: CompensationDeliveryContentRequest,
  }),
})

export type UpdateCompensationDeliveryMasterContentRequest = z.infer<typeof UpdateCompensationDeliveryMasterContentRequest>

export const UpdateConfiscationDeliveryMasterContentRequest = z.object({
  name: z.string().min(1).max(20).describe("回収名"),
  executionStartAt: z.number().describe("配信開始日時 (UNIX Timestamp mills)"),
  payload: z.object({
    notification: z.object({
      title: z.string().describe("お知らせタイトル"),
      icon: PersonalNotificationIcon.describe("アイコン"),
      content: z.any().describe("QuillDelta形式のリッチテキスト"),
    }),
    deliveryContent: z.object({
      coin: z.object({
        scope: z.enum(Scope).describe("コインスコープ"),
        amount: z.number().int().min(1).max(99999).describe("回収する金額（正の値）"),
      }),
    }),
  }),
})

export type UpdateConfiscationDeliveryMasterContentRequest = z.infer<typeof UpdateConfiscationDeliveryMasterContentRequest>

export const UpdatePushDeliveryMasterContentRequest = z.object({
  executionStartAt: z.number(),
  payload: z.object({
    title: z.string().min(1),
    body: z.string().min(1),
    managedImageId: z.string().nullable(),
    action: PushDeliveryActionRequest.nullable(),
  }),
})

export type UpdatePushDeliveryMasterContentRequest = z.infer<typeof UpdatePushDeliveryMasterContentRequest>

export const UpdateBulkDeliveryMasterTargetSettingRequest = z.object({
  targetSetting: BulkDeliveryTargetSettingRequest.describe("配信対象設定"),
})

export type UpdateBulkDeliveryMasterTargetSettingRequest = z.infer<typeof UpdateBulkDeliveryMasterTargetSettingRequest>
